import React from 'react';

// Touch-and-hold dragging for elements that use the browser's drag-and-drop events.
//
// A finger that rests on a draggable element for HOLD_MS without moving picks it up. The touch is then
// replayed as the events a mouse drag produces (dragstart; dragenter, dragover and dragleave on whatever is
// under the finger; drop and dragend), so one set of handlers serves mouse and touch. Only the finger that
// started the drag moves and drops it; other fingers touching or lifting do not. Browsers that start their own
// touch drag (iOS Safari, Chrome on Android) are held back for the length of each touch, so every browser
// behaves alike, including Firefox on Android, which has none, while a mouse or trackpad still drags natively.

const HOLD_MS = 400;
/** Moving further than this before the hold completes is a scroll, not a pick-up. */
const MOVE_TOLERANCE = 8;
/** Holding the item this close to a scroll area's edge scrolls it. */
const SCROLL_EDGE = 48;
const SCROLL_STEP = 12;
/** A resting mouse keeps receiving dragover; so does a resting finger, which keeps hover-to-open timers working. */
const OVER_INTERVAL_MS = 80;

/** Draggable rows add this: on touch-first devices the long-press menu and text selection would fight the
 *  touch-and-hold drag. It leaves native dragging alone, so a mouse or trackpad (an iPad trackpad) still drags. */
export const touchDragSourceSx = {
    '@media (hover: none) and (pointer: coarse)': { WebkitTouchCallout: 'none', userSelect: 'none' },
} as const;

interface TouchDrag {
    source: HTMLElement;
    transfer: DataTransfer;
    scroller: HTMLElement;
    preview: HTMLElement;
    over: Element | null;
    accepted: boolean;
    x: number;
    y: number;
    offsetX: number;
    offsetY: number;
    frame: number;
    lastOver: number;
}

function scrollableAncestor(element: Element): HTMLElement {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const overflow = getComputedStyle(node).overflowY;
        if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node;
    }
    return (document.scrollingElement ?? document.documentElement) as HTMLElement;
}

/** A lifted copy of the item that follows the finger, as the drag image follows a mouse. */
function liftedCopy(source: HTMLElement, rect: DOMRect): HTMLElement {
    const preview = document.createElement('div');
    const copy = source.cloneNode(true) as HTMLElement;
    copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
    preview.appendChild(copy);
    preview.setAttribute('aria-hidden', 'true');
    Object.assign(preview.style, {
        position: 'fixed', left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, maxHeight: '120px', overflow: 'hidden',
        pointerEvents: 'none', zIndex: '2000', opacity: '0.92', background: '#fff', borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(22, 58, 99, 0.25)',
    });
    return preview;
}

/** Lets touch-and-hold pick up any `draggable` element inside `rootRef`, then drag it like a mouse would. */
export function useTouchDragAndDrop(rootRef: React.RefObject<HTMLElement | null>) {
    React.useEffect(() => {
        const root = rootRef.current;
        if (!root || typeof DragEvent === 'undefined' || typeof DataTransfer === 'undefined') return undefined;
        let pending: { source: HTMLElement; x: number; y: number; timer: number } | null = null;
        let drag: TouchDrag | null = null;

        const fire = (current: TouchDrag, target: Element, type: string, related: Element | null = null) => {
            const event = new DragEvent(type, { bubbles: true, cancelable: true, composed: true,
                clientX: current.x, clientY: current.y, dataTransfer: current.transfer, relatedTarget: related });
            target.dispatchEvent(event);
            return event;
        };
        const dragOver = (current: TouchDrag) => {
            const target = document.elementFromPoint(current.x, current.y);
            if (target !== current.over) {
                if (current.over) fire(current, current.over, 'dragleave', target);
                if (target) fire(current, target, 'dragenter', current.over);
                current.over = target;
            }
            current.accepted = target ? fire(current, target, 'dragover').defaultPrevented : false;
            current.lastOver = performance.now();
        };
        const tick = () => {
            const current = drag;
            if (!current) return;
            const { scroller } = current;
            const area = scroller === document.scrollingElement ? { top: 0, bottom: window.innerHeight } : scroller.getBoundingClientRect();
            const top = Math.max(area.top, 0);
            const bottom = Math.min(area.bottom, window.innerHeight);
            const step = current.y < top + SCROLL_EDGE ? -SCROLL_STEP : current.y > bottom - SCROLL_EDGE ? SCROLL_STEP : 0;
            if (step) scroller.scrollTop += step;
            if (step || performance.now() - current.lastOver > OVER_INTERVAL_MS) dragOver(current);
            current.frame = requestAnimationFrame(tick);
        };
        const cancelPending = () => {
            if (pending) window.clearTimeout(pending.timer);
            pending = null;
        };
        const start = () => {
            if (!pending) return;
            const { source, x, y } = pending;
            pending = null;
            const rect = source.getBoundingClientRect();
            const current: TouchDrag = { source, transfer: new DataTransfer(), scroller: scrollableAncestor(source), preview: liftedCopy(source, rect),
                over: null, accepted: false, x, y, offsetX: x - rect.left, offsetY: y - rect.top, frame: 0, lastOver: 0 };
            // A handler that refuses the drag (for example while saving) prevents dragstart, as with a mouse.
            if (fire(current, source, 'dragstart').defaultPrevented) return;
            drag = current;
            document.body.appendChild(current.preview);
            navigator.vibrate?.(10);
            dragOver(current);
            current.frame = requestAnimationFrame(tick);
        };
        const finish = (dropped: boolean) => {
            const current = drag;
            if (!current) return;
            cancelAnimationFrame(current.frame);
            current.preview.remove();
            if (dropped && current.over && current.accepted) fire(current, current.over, 'drop');
            fire(current, current.source, 'dragend');
            drag = null;
        };

        // The finger that may pick a row up, and the rows set to draggable="false" while it is down: for the length
        // of the touch the browser cannot start its own drag from them. A mouse never touches, so it still drags natively.
        let tracked: { id: number; suspended: HTMLElement[] } | null = null;
        const trackedTouch = (list: TouchList) => {
            const id = tracked?.id;
            return id === undefined ? undefined : Array.from(list).find(touch => touch.identifier === id);
        };
        const endTracking = () => {
            tracked?.suspended.forEach(element => element.setAttribute('draggable', 'true'));
            tracked = null;
        };

        const onTouchStart = (event: TouchEvent) => {
            // A second finger is another gesture, such as a pinch: it cancels a pick-up that has not happened yet.
            cancelPending();
            // A lone finger means any earlier touch has ended.
            if (event.touches.length === 1 && !drag) endTracking();
            if (drag || tracked || event.touches.length !== 1) return;
            const source = (event.target as Element | null)?.closest<HTMLElement>('[draggable="true"]');
            if (!source || !root.contains(source)) return;
            const suspended: HTMLElement[] = [];
            for (let row: HTMLElement | null = source; row && root.contains(row); row = row.parentElement?.closest<HTMLElement>('[draggable="true"]') ?? null) {
                row.setAttribute('draggable', 'false');
                suspended.push(row);
            }
            const touch = event.changedTouches[0];
            tracked = { id: touch.identifier, suspended };
            pending = { source, x: touch.clientX, y: touch.clientY, timer: window.setTimeout(start, HOLD_MS) };
        };
        const onTouchMove = (event: TouchEvent) => {
            const touch = trackedTouch(event.touches);
            if (!touch) return;
            if (pending && Math.hypot(touch.clientX - pending.x, touch.clientY - pending.y) > MOVE_TOLERANCE) cancelPending();
            const current = drag;
            if (!current) return;
            // The page must not scroll under a lifted item; the edges scroll it instead.
            event.preventDefault();
            current.x = touch.clientX;
            current.y = touch.clientY;
            current.preview.style.left = `${current.x - current.offsetX}px`;
            current.preview.style.top = `${current.y - current.offsetY}px`;
            dragOver(current);
        };
        const onTouchEnd = (event: TouchEvent) => {
            // Only the finger that started the touch ends it; another finger lifting changes nothing.
            if (!trackedTouch(event.changedTouches)) return;
            cancelPending();
            endTracking();
            if (!drag) return;
            // No click on whatever row the item was dropped on.
            event.preventDefault();
            finish(event.type === 'touchend');
        };
        // A browser that starts its own touch drag anyway, or its long-press menu, would compete with this one.
        const blockNativeDrag = (event: DragEvent) => {
            if (event.isTrusted && (tracked || drag)) { event.preventDefault(); event.stopPropagation(); }
        };
        const blockMenu = (event: Event) => { if (pending || drag) event.preventDefault(); };

        // On the document, so a second finger anywhere on the screen is noticed; only rows inside `root` are picked up.
        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: false });
        document.addEventListener('touchend', onTouchEnd, { passive: false });
        document.addEventListener('touchcancel', onTouchEnd, { passive: false });
        document.addEventListener('dragstart', blockNativeDrag, true);
        document.addEventListener('contextmenu', blockMenu, true);
        return () => {
            cancelPending();
            finish(false);
            endTracking();
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            document.removeEventListener('touchcancel', onTouchEnd);
            document.removeEventListener('dragstart', blockNativeDrag, true);
            document.removeEventListener('contextmenu', blockMenu, true);
        };
    }, [rootRef]);
}
